import type { Dispatch } from 'redux'
import * as DataActions from '../actions/Data'
import { getJSON, postJSON } from '../common/services/api'
import { normalizeErrorMessage, showToast } from '../common/utils'
import { loadList, updateListItem } from './Data'

const LIST_ID = 'emar-round-timings'

/**
 * eMAR's own round-timings resource, behind the `emar_v2_round_timings` flag.
 * With the flag off every function here returns the thunk the caller used
 * before, so the legacy path is the same code as it has always been (ROST-3869).
 *
 * Creating goes through `EmarRoundTimingModal`, which posts the form rather
 * than calling this module, so the URL lives there — gated on the same flag.
 * **ROST-3916 must be deployed before the flag is switched on for any home**:
 * until then eMAR's create saves without the audit fields its schema requires
 * and the route 500s.
 */
export const EMAR_ROUND_TIMINGS_V2_URL = '/emar/emar-round-timings'

/** V2 answers with the mongo document: `_id`, and no `{ result }` envelope. */
type V2RoundTiming = {
  _id: string
  time: string
  title?: string
  deleted?: boolean
}

/**
 * Everything reading this list keys off `id` — the data reducer even dedupes on
 * it — so the document is mapped here rather than in each screen's selectors.
 *
 * `deleted` is defaulted for the same reason the legacy route sent
 * `doc.deleted || false`: consumers filter with `{ deleted: false }`, which
 * matches on equality, so a document that omits the field would be dropped from
 * the list rather than shown.
 */
const toListItem = (timing: V2RoundTiming) => ({
  ...timing,
  id: timing._id,
  deleted: timing.deleted ?? false
})

/**
 * Loads the home's round timings.
 *
 * `legacyUrl` is whatever the caller passed before this flag existed: undefined
 * for care-management's `list/emar-round-timings`, or eMAR's legacy-compatible
 * route on the screens already gated on `emar_v2_chart`.
 */
export function loadEmarRoundTimings({
  useV2Endpoints,
  legacyUrl
}: {
  useV2Endpoints: boolean
  legacyUrl?: string
}) {
  if (!useV2Endpoints) return loadList(LIST_ID, undefined, true, legacyUrl)

  return async (dispatch: Dispatch) => {
    try {
      const timings: Array<V2RoundTiming> = await getJSON({
        url: EMAR_ROUND_TIMINGS_V2_URL
      })
      dispatch(DataActions.setLoadedList(LIST_ID, timings.map(toListItem)))
    } catch (err) {
      console.log(err)
      showToast({ type: 'error', title: 'Something went wrong' })
    }
  }
}

/**
 * Removes a round timing.
 *
 * This is a soft delete, as it was on the legacy route: the timing stays on
 * medications already using it and only stops being offered for new ones, which
 * is what the confirmation copy promises. V2's `DELETE` route removes the
 * document outright, so this PATCHes the same `deleted` flag instead.
 */
export function removeEmarRoundTiming({
  itemId,
  successMsg,
  useV2Endpoints
}: {
  itemId: string
  successMsg?: string
  useV2Endpoints: boolean
}) {
  if (!useV2Endpoints) {
    return updateListItem({
      listId: LIST_ID,
      itemId,
      successMsg,
      json: { deleted: true },
      // Untyped JS: `updateListItem` destructures `url` and falls back to the
      // `list/` route when it is absent, which is the legacy behaviour here.
      url: undefined
    })
  }

  return async (dispatch: Dispatch) => {
    try {
      const timing: V2RoundTiming = await postJSON({
        url: `${EMAR_ROUND_TIMINGS_V2_URL}/${itemId}`,
        method: 'PATCH',
        json: { deleted: true }
      })
      if (successMsg) showToast({ title: successMsg })
      dispatch(DataActions.setLoadedListItem(LIST_ID, toListItem(timing)))
    } catch (err) {
      showToast({ type: 'error', title: normalizeErrorMessage(err) })
      console.log(err)
    }
  }
}
